import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Select, Button, Table, Badge, List, Grid, Typography, Input, Tag, Avatar } from 'antd';
import { ExportOutlined, RightOutlined, SearchOutlined } from '@ant-design/icons';
import { useFindAllEntityMasterMutation } from '../../redux-toolkit/services/entityMasterService';
import { useGetDashboardDataMutation } from '../../redux-toolkit/services/dashboardService';
import { getLocalStorageData } from '../../utils/LocalStorageService';
import './dashboard.scss';

const { Option } = Select;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const SuccessionDashboard = () => {
    const screens = useBreakpoint();
    const [selectedEntity, setSelectedEntity] = useState<string | null>(null);
    const [entities, setEntities] = useState<any[]>([]);

    // Search state for the succession table
    const [searchText, setSearchText] = useState('');

    const localUserObj: any = getLocalStorageData("localUser");
    const token = localUserObj?.token;

    // API Hooks
    // Using findAll for the dropdown list and heir list base
    const [findAllEntities, { data: allEntitiesData, isLoading: isEntitiesLoading }] = useFindAllEntityMasterMutation();
    const [getDashboardData, { data: dashboardData, isLoading: isDashboardLoading }] = useGetDashboardDataMutation();

    useEffect(() => {
        if (token) {
            findAllEntities({ token });
        }
    }, [token]);

    useEffect(() => {
        if (allEntitiesData?.data) {
            setEntities(allEntitiesData.data);
            if (!selectedEntity && allEntitiesData.data.length > 0) {
                setSelectedEntity(allEntitiesData.data[0].id);
            }
        }
    }, [allEntitiesData]);

    useEffect(() => {
        if (selectedEntity && token) {
            getDashboardData({ payload: { entityId: selectedEntity }, token });
        }
    }, [selectedEntity, token]);

    const handleEntityChange = (value: string) => {
        setSelectedEntity(value);
    };

    // Derived Data
    const currentEntity = entities.find(e => e.id === selectedEntity);
    const entityName = currentEntity?.entityName || 'Select Entity';

    // -- Mock Data for Sidebar (Shared with Overview) --
    const alerts = [
        { id: 1, text: 'Project at risk: Retail OmniPilot (Isha Ambani) — requires attention.', type: 'warning' },
        { id: 2, text: 'Project at risk: Green Hydrogen Pilot (Anant Ambani) — requires attention.', type: 'warning' },
        { id: 3, text: 'Audit Findings: Environmental Review has 3 findings.', type: 'error' }
    ];
    const quickStats = [
        { label: 'Heirs', value: 5, color: '#e6f7ff', textColor: '#1890ff' },
        { label: 'Assets', value: 4, color: '#f6ffed', textColor: '#52c41a' },
        { label: 'Projects', value: 3, color: '#fff7e6', textColor: '#fa8c16' },
        { label: 'Audits', value: 3, color: '#fff1f0', textColor: '#f5222d' },
    ];

    // -- Mock Data for Succession Table --
    // In a real app, this would come from a dedicated succession API endpoint
    // mapped from the Entity Master.
    const generateMockScores = (seed: number) => {
        const scores = [3, 4, 5];
        return scores[seed % 3];
    };
    const mockPlanStatus = (seed: number) => {
        const plans = ['Planned', 'Managed', 'Action Required'];
        return plans[seed % 3];
    };

    const successionData = entities
        .filter(e => e.entityName.toLowerCase().includes(searchText.toLowerCase()))
        .slice(0, 5) // Just showing top 5 for demo
        .map((e, idx) => ({
            key: e.id,
            heir: { name: e.entityName, role: e.entityType || 'Potential Successor' },
            scores: {
                overall: generateMockScores(idx),
                technical: generateMockScores(idx + 1),
                people: generateMockScores(idx + 2),
                values: generateMockScores(idx + 3),
            },
            plan: mockPlanStatus(idx),
        }));

    const renderScore = (score: number) => {
        let color = '#52c41a'; // Green (5)
        if (score === 4) color = '#1890ff'; // Blue
        if (score === 3) color = '#faad14'; // Yellow
        if (score < 3) color = '#f5222d'; // Red

        return (
            <div style={{
                width: '28px', height: '28px', borderRadius: '50%', backgroundColor: color,
                color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 'bold', margin: '0 auto'
            }}>
                {score}
            </div>
        );
    };

    const renderPlanStatus = (status: string) => {
        let color = 'default';
        if (status === 'Planned') color = 'success';
        if (status === 'Managed') color = 'warning';
        if (status === 'Action Required') color = 'error';
        return <Tag color={color}>{status}</Tag>;
    };

    return (
        <div className="p-4" style={{ backgroundColor: '#f0f2f5', minHeight: '100vh' }}>
            {/* Header Section */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <Title level={2} style={{ marginBottom: 0 }}>
                        {entityName} — Reliance Family Office Prototype
                    </Title>
                    <Text type="secondary">Single master dashboard • Succession • Wealth • Projects • CAP Table • Audits • Mentorship</Text>
                    <div style={{ marginTop: '8px' }} className="d-flex align-items-center text-muted">
                        <span>Dashboard</span> <RightOutlined style={{ fontSize: '10px', margin: '0 8px' }} /> <span className="text-primary">Succession</span>
                    </div>
                </div>
                <div className="d-flex gap-3">
                    <Select
                        showSearch
                        placeholder="Search across dashboard..."
                        optionFilterProp="children"
                        onChange={handleEntityChange}
                        onSearch={(val) => console.log('search:', val)}
                        filterOption={(input, option) =>
                            (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase())
                        }
                        style={{ width: 300 }}
                        value={selectedEntity}
                        loading={isEntitiesLoading}
                    >
                        {entities.map(e => (
                            <Option key={e.id} value={e.id}>{e.entityName}</Option>
                        ))}
                    </Select>
                    <Button type="primary" icon={<ExportOutlined />}>Export / Print</Button>
                </div>
            </div>

            {/* Main Content Grid */}
            <Row gutter={[24, 24]}>
                {/* Left/Main Column - Succession Table */}
                <Col span={24}>
                    <Card className="mb-4 shadow-sm" bordered={false} bodyStyle={{ padding: '24px' }}>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <Title level={4} className="mb-0">Succession Dashboard</Title>
                            <div className="d-flex gap-2">
                                <Input
                                    prefix={<SearchOutlined />}
                                    placeholder="Search heir name or role..."
                                    style={{ width: 200 }}
                                    onChange={(e) => setSearchText(e.target.value)}
                                />
                                <Select defaultValue="All Risk Levels" style={{ width: 140 }}>
                                    <Option value="All Risk Levels">All Risk Levels</Option>
                                    <Option value="High">High Risk</Option>
                                    <Option value="Low">Low Risk</Option>
                                </Select>
                                <Button type="default" className="bg-success text-white border-success">Export CSV</Button>
                            </div>
                        </div>

                        <Text type="secondary" className="mb-3 d-block">Showing {successionData.length} of {successionData.length} heirs</Text>

                        <Table
                            dataSource={successionData}
                            pagination={false}
                            columns={[
                                {
                                    title: 'HEIR',
                                    key: 'heir',
                                    width: 250,
                                    render: (_, record) => (
                                        <div className="d-flex align-items-center">
                                            {/* Placeholder Avatar */}
                                            {/* <Avatar style={{ backgroundColor: '#f56a00', marginRight: '8px' }}>{record.heir.name[0]}</Avatar> */}
                                            <div>
                                                <Text strong className="d-block">{record.heir.name}</Text>
                                                <Text type="secondary" style={{ fontSize: '12px' }}>{record.heir.role}</Text>
                                            </div>
                                        </div>
                                    )
                                },
                                {
                                    title: 'OVERALL',
                                    key: 'overall',
                                    align: 'center',
                                    render: (_, record) => renderScore(record.scores.overall)
                                },
                                {
                                    title: 'TECHNICAL',
                                    key: 'technical',
                                    align: 'center',
                                    render: (_, record) => renderScore(record.scores.technical)
                                },
                                {
                                    title: 'PEOPLE',
                                    key: 'people',
                                    align: 'center',
                                    render: (_, record) => renderScore(record.scores.people)
                                },
                                {
                                    title: 'VALUES',
                                    key: 'values',
                                    align: 'center',
                                    render: (_, record) => renderScore(record.scores.values)
                                },
                                {
                                    title: 'PLAN',
                                    key: 'plan',
                                    align: 'center',
                                    render: (_, record) => renderPlanStatus(record.plan)
                                },
                                {
                                    title: 'ACTION',
                                    key: 'action',
                                    align: 'center',
                                    render: () => <a href="#">View Details</a>
                                }
                            ]}
                        />
                        <div className="mt-4">
                            <Text strong>▶ Succession Policy & Recommendations</Text>
                        </div>
                    </Card>
                </Col>                
            </Row>
        </div>
    );
};

export default SuccessionDashboard;
